import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import { API_URL } from "config";
import Icon from "@mui/material/Icon";
import { useAuth } from "../../../AuthContext";
import { hasPermission, mergeGrantedPermissions } from "utils/granularPermissions";
import { apiErrorMessage, apiSuccessMessage, readJsonSafely } from "utils/apiResponseMessages";
import { shiftDisplayLabel } from "utils/shiftDisplay";

// Initialize blank form data
const initialFormData = {
  name: "",
  email: "",
  password: "",
  password_confirmation: "",
  branch_id: "",
  subbranch_id: "",
  shift_id: "",
  team_leader_id: "",
  roles: [],
  user_code: "",
  weekend_off: [],
  status: "",
  joining_date: "",
  appointment_date: "",
};

const weekendOffOptions = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const normalizeWeekendOffValue = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.filter((day) => weekendOffOptions.includes(day));
  }

  if (typeof value === "string") {
    try {
      const decoded = JSON.parse(value);
      if (Array.isArray(decoded)) {
        return decoded.filter((day) => weekendOffOptions.includes(day));
      }
    } catch (error) {
      // Fall back to comma/pipe-separated legacy strings.
    }

    return value
      .split(/[,|]/)
      .map((day) => day.trim())
      .filter((day) => weekendOffOptions.includes(day));
  }

  return [];
};

const AddUser = () => {
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const [formData, setFormData] = useState(initialFormData);
  const [branches, setBranches] = useState([]);
  const [subBranches, setSubBranches] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  const grantedPermissions = useMemo(
    () => mergeGrantedPermissions(permissions, userPermission),
    [permissions, userPermission]
  );
  const hasUsersPermission = useCallback(
    (...requiredPermissions) => hasPermission(grantedPermissions, ...requiredPermissions),
    [grantedPermissions]
  );
  const canViewPage = hasUsersPermission("users.pages.create.view");
  const canSubmit =
    hasUsersPermission("users.actions.create") &&
    hasUsersPermission("users.actions.submit_create");
  const canOpenList =
    hasUsersPermission("users.pages.list.view") &&
    hasUsersPermission("users.actions.open_list");
  const canOpenTrashed =
    hasUsersPermission("users.pages.trashed.view") &&
    hasUsersPermission("users.actions.open_trashed");
  const canViewField = useCallback(
    (field) => hasUsersPermission(`users.fields.${field}.view`),
    [hasUsersPermission]
  );

  const selectedRole = useMemo(
    () => availableRoles.find((role) => String(role.id) === String(formData.roles[0] || "")),
    [availableRoles, formData.roles]
  );
  const hasParentRole = Boolean(selectedRole?.parent_role_id);
  const requiresReportingUser =
    hasParentRole && selectedRole?.requires_reporting_user !== false;

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem("authToken");
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      const errorData = await readJsonSafely(response);
      throw new Error(apiErrorMessage(errorData, `Request failed (${response.status})`));
    }
    return readJsonSafely(response);
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;

    if (!canViewPage) {
      return;
    }

    // Reset all form data when component mounts
    resetForm();

    const loadInitialData = async () => {
      try {
        const [branchesData, rolesData] = await Promise.all([
          canViewField("branch")
            ? fetchWithAuth(`${API_URL}/users/options/branches`)
            : Promise.resolve({ data: [] }),
          canViewField("role")
            ? fetchWithAuth(`${API_URL}/users/options/roles`)
            : Promise.resolve({ roles: [] }),
        ]);

        setBranches(branchesData.data || []);

        setAvailableRoles(rolesData.roles || []);
      } catch (err) {
        setSnackbar({
          open: true,
          message: "Failed to load initial data",
          color: "error",
        });
      }
    };
    loadInitialData();
  }, [canViewField, canViewPage, fetchWithAuth, isAuthReady]);

  const resetForm = () => {
    setFormData(initialFormData);
    setSubBranches([]);
    setShifts([]);
    setTeamLeaders([]);

  };

  useEffect(() => {
    const loadBranchData = async () => {
      if (!formData.branch_id) {
        setSubBranches([]);
        setShifts([]);
        return;
      }

      try {
        const subBranchesData = canViewField("sub_branch")
          ? await fetchWithAuth(`${API_URL}/users/options/sub-branches/${formData.branch_id}`)
          : { data: [] };
        
        setSubBranches(subBranchesData.data || subBranchesData.subbranches || subBranchesData || []);
      } catch (err) {
        setSnackbar({
          open: true,
          message: "Failed to load branch data",
          color: "error",
        });
      }
    };
    loadBranchData();
  }, [canViewField, fetchWithAuth, formData.branch_id]);

  useEffect(() => {
    const loadShifts = async () => {
      if (!formData.subbranch_id) {
        setShifts([]);
        return;
      }
      try {
        const shiftsData = await fetchWithAuth(`${API_URL}/users/options/shifts/${formData.subbranch_id}`);
        setShifts(shiftsData.data || shiftsData.shifts || shiftsData || []);
      } catch (err) {
        setSnackbar({
          open: true,
          message: "Failed to load shifts",
          color: "error",
        });
      }
    };
    loadShifts();
  }, [fetchWithAuth, formData.subbranch_id]);

  useEffect(() => {
    const loadTeamLeaders = async () => {
      if (
        !canViewField("team_leader") ||
        !selectedRole?.id ||
        !requiresReportingUser ||
        !formData.branch_id ||
        !formData.subbranch_id ||
        !formData.shift_id
      ) {
        setTeamLeaders([]);
        return;
      }
      try {
        const reportingParams = new URLSearchParams({
          role_id: String(selectedRole.id),
          ...(formData.branch_id ? { branch_id: String(formData.branch_id) } : {}),
          ...(formData.subbranch_id ? { subbranch_id: String(formData.subbranch_id) } : {}),
          ...(formData.shift_id ? { shift_id: String(formData.shift_id) } : {}),
        });
        const data = await fetchWithAuth(
          `${API_URL}/users/options/team-leaders?${reportingParams.toString()}`
        );
        setTeamLeaders(data.data || data.team_leaders || data || []);
      } catch (err) {
        setSnackbar({
          open: true,
          message: "Failed to load team leaders",
          color: "error",
        });
      }
    };
    loadTeamLeaders();
  }, [
    fetchWithAuth,
    canViewField,
    formData.branch_id,
    formData.shift_id,
    formData.subbranch_id,
    requiresReportingUser,
    selectedRole?.id,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === "branch_id") {
        newData.subbranch_id = "";
        newData.shift_id = "";
        newData.team_leader_id = "";
      } else if (name === "subbranch_id") {
        newData.shift_id = "";
      }
      return newData;
    });
  };

  const handleShiftChange = (e) => {
    const shiftId = e.target.value;
    setFormData((prev) => ({ ...prev, shift_id: shiftId, team_leader_id: "" }));
  };

  const handleRoleChange = (e) => {
    const selectedRoleId = e.target.value;

    // Reset dependent fields
    setFormData((prev) => ({
      ...initialFormData,
      name: prev.name,
      email: prev.email,
      password: prev.password,
      password_confirmation: prev.password_confirmation,
      roles: [selectedRoleId],
    }));
  };

  const handleWeekendOffToggle = (day) => {
    setFormData((prev) => {
      const selectedDays = normalizeWeekendOffValue(prev.weekend_off);
      const nextDays = selectedDays.includes(day)
        ? selectedDays.filter((selectedDay) => selectedDay !== day)
        : [...selectedDays, day];

      return { ...prev, weekend_off: nextDays };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setSnackbar({ open: true, message: "You do not have permission to create users", color: "error" });
      return;
    }

    setLoading(true);

    if (!formData.user_code) {
      setSnackbar({ open: true, message: "Employee Code is required", color: "error" });
      setLoading(false);
      return;
    }

    if (!formData.status) {
      setSnackbar({ open: true, message: "Status is required", color: "error" });
      setLoading(false);
      return;
    }

    // 🔒 Status based date validation
    if (formData.status === "training") {
      if (!formData.joining_date) {
        setSnackbar({
          open: true,
          message: "Joining Date is required for Training status",
          color: "error",
        });
        setLoading(false);
        return;
      }
    }

    if (formData.status === "selected") {
      if (!formData.joining_date || !formData.appointment_date) {
        setSnackbar({
          open: true,
          message: "Joining Date and Appointment Date are required for Selected status",
          color: "error",
        });
        setLoading(false);
        return;
      }
    }


    try {

      const toYMD = (date) => {
        if (!date) return null;
        return new Date(date).toISOString().slice(0, 10);
      };

      const payload = {
        ...formData,

        joining_date: toYMD(formData.joining_date),
        appointment_date: toYMD(formData.appointment_date),
        branch_id: formData.branch_id ? parseInt(formData.branch_id) : null,
        subbranch_id: formData.subbranch_id ? parseInt(formData.subbranch_id) : null,
        team_leader_id: formData.team_leader_id ? parseInt(formData.team_leader_id) : null,
        weekend_off: normalizeWeekendOffValue(formData.weekend_off),
      };

      payload.shift_id = formData.shift_id ? parseInt(formData.shift_id) : null;

      const data = await fetchWithAuth(`${API_URL}/users`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setSnackbar({
        open: true,
        message: apiSuccessMessage(data, "User added successfully"),
        color: "success",
      });
      setTimeout(() => {
        resetForm();
        navigate("/users/list");
      }, 1500);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message,
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Field disabled states
  const isBranchDisabled = !formData.roles.length;
  const isSubBranchDisabled = !formData.branch_id;
  const isShiftDisabled = !formData.subbranch_id;
  const needsReportingUser = requiresReportingUser;
  const isTeamLeaderDisabled =
    !requiresReportingUser ||
    !formData.branch_id ||
    !formData.subbranch_id ||
    !formData.shift_id;

  if (!isAuthReady) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="body2">Loading...</SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="medium">
            Add New User
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to add users.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="User"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar({ ...snackbar, open: false })}
        time={3000}
      />
      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Add New User
          </SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canOpenList && (
              <SoftButton
                color="info"
                onClick={() => navigate("/users/list")}
                startIcon={<Icon>list</Icon>}
              >
                User List
              </SoftButton>
            )}
            {canOpenTrashed && (
              <SoftButton
                color="dark"
                variant="outlined"
                onClick={() => navigate("/users/trashed")}
                startIcon={<Icon>delete</Icon>}
              >
                Trashed List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>
        <SoftBox component="form" onSubmit={handleSubmit} autoComplete="off" noValidate>
          <SoftBox
            display="flex"
            gap={5}
            flexWrap="wrap"
            sx={{ flexDirection: { xs: "column", sm: "row" } }}
          >
            {/* Name Field */}
            {canViewField("name") && (
            <SoftBox mb={2} sx={{ width: { xs: "100%", sm: "45%" } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Name
              </SoftTypography>
              <SoftInput
                type="text"
                size="small"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                autoComplete="off"
                fullWidth
              />
            </SoftBox>
            )}

            {/* Email Field */}
            {canViewField("email") && (
            <SoftBox mb={2} sx={{ width: { xs: "100%", sm: "45%" } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Email
              </SoftTypography>
              <SoftInput
                type="email"
                name="email"
                size="small"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="off"
                fullWidth
              />
            </SoftBox>
            )}
          </SoftBox>

          <SoftBox
            display="flex"
            gap={5}
            flexWrap="wrap"
            sx={{ flexDirection: { xs: "column", sm: "row" } }}
          >
            {/* Password Field */}
            {canViewField("password") && (
            <SoftBox mb={2} sx={{ width: { xs: "100%", sm: "45%" } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Password
              </SoftTypography>
              <SoftInput
                type="password"
                name="password"
                size="small"
                value={formData.password}
                onChange={handleChange}
                required
                fullWidth
                autoComplete="new-password"
              />
            </SoftBox>
            )}

            {/* Confirm Password Field */}
            {canViewField("password_confirmation") && (
            <SoftBox mb={2} sx={{ width: { xs: "100%", sm: "45%" } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Confirm Password
              </SoftTypography>
              <SoftInput
                size="small"
                type="password"
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={handleChange}
                required
                fullWidth
                autoComplete="new-password"
              />
            </SoftBox>
            )}
          </SoftBox>

          <SoftBox
            display="flex"
            gap={5}
            flexWrap="wrap"
            sx={{ flexDirection: { xs: "column", sm: "row" } }}
          >
            {/* Role Selection */}
            {canViewField("role") && (
            <SoftBox mb={2} sx={{ width: { xs: "100%", sm: "45%" } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Role
              </SoftTypography>
              <select
                name="roles"
                value={formData.roles[0] || ""}
                onChange={handleRoleChange}
                required
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  outline: "none",
                  padding: "8px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  height: "36px",
                  border: "1px solid #d2d6da",
                }}
              >
                <option value="">Select Role</option>
                {availableRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </SoftBox>
            )}

            {/* Branch Selection */}
            {canViewField("branch") && (
            <SoftBox mb={2} sx={{ width: { xs: "100%", sm: "45%" } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Branch
              </SoftTypography>
              <select
                name="branch_id"
                value={formData.branch_id}
                onChange={handleChange}
                required
                disabled={isBranchDisabled}
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  outline: "none",
                  padding: "8px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  height: "36px",
                  border: "1px solid #d2d6da",
                }}
              >
                <option value="">Select Branch</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </SoftBox>
            )}
          </SoftBox>

          <SoftBox
            display="flex"
            gap={5}
            flexWrap="wrap"
            sx={{ flexDirection: { xs: "column", sm: "row" } }}
          >
            {/* Sub Branch Selection */}
            {canViewField("sub_branch") && (
            <SoftBox mb={2} sx={{ width: { xs: "100%", sm: "45%" } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Sub Branch
              </SoftTypography>
              <select
                name="subbranch_id"
                value={formData.subbranch_id}
                onChange={handleChange}
                required
                disabled={isSubBranchDisabled}
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  outline: "none",
                  padding: "8px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  height: "36px",
                  border: "1px solid #d2d6da",
                  backgroundColor: isSubBranchDisabled ? "#fff" : "white",
                }}
              >
                <option value="">Select Sub Branch</option>
                {subBranches.map((sb) => (
                  <option key={sb.id} value={sb.id}>
                    {sb.name}
                  </option>
                ))}
              </select>
            </SoftBox>
            )}

            {/* Shift */}
            {canViewField("shift") && selectedRole && (
              <SoftBox sx={{ width: { xs: "100%", sm: "45%" } }}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Shift
                </SoftTypography>
                <select
                  name="shift_id"
                  value={formData.shift_id}
                  onChange={handleShiftChange}
                  disabled={isShiftDisabled}
                  required
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "10px",
                    border: "1px solid #d2d6da",
                    fontSize: "14px",
                    outline: "none",
                  }}
                >
                  <option value="">Select Shift</option>
                  {shifts.map((shift) => (
                    <option key={shift.id} value={shift.id}>
                      {shiftDisplayLabel(shift)}
                    </option>
                  ))}
                </select>
              </SoftBox>
            )}

            {/* Reporting User Selection */}
            {canViewField("team_leader") && needsReportingUser && (
              <SoftBox sx={{ width: { xs: "100%", sm: "45%" } }}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Team Leader / Reporting User
                </SoftTypography>
                <select
                  name="team_leader_id"
                  value={formData.team_leader_id}
                  onChange={handleChange}
                  required={needsReportingUser}
                  disabled={isTeamLeaderDisabled}
                  style={{
                    width: "100%",
                    padding: "8px",
                    outline: "none",
                    borderRadius: "10px",
                    border: "1px solid #d2d6da",
                    fontSize: "14px",
                    backgroundColor: isTeamLeaderDisabled ? "#fff" : "white",
                  }}
                >
                  <option value="">Select Reporting User</option>
                  {teamLeaders.map((leader) => (
                    <option key={leader.id} value={leader.id}>
                      {leader.name}
                    </option>
                  ))}
                </select>
              </SoftBox>
            )}

            {/* Status */}
            {canViewField("status") && (
            <SoftBox sx={{ width: { xs: "100%", sm: "45%" } }}>
              <SoftTypography variant="caption" fontWeight="bold">
                Status
              </SoftTypography>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "8px",
                  outline: "none",
                  borderRadius: "10px",
                  border: "1px solid #d2d6da",
                  fontSize: "14px",
                  backgroundColor: isTeamLeaderDisabled ? "#fff" : "white",
                }}
              >
                <option value="">Select Status</option>
                <option value="training">Training</option>
                <option value="selected">Selected</option>
              </select>
            </SoftBox>
            )}
          </SoftBox>





          {(canViewField("joining_date") || canViewField("appointment_date")) &&
            (formData.status === "training" || formData.status === "selected") && (

            <SoftBox
              display="flex"
              gap={5}
              flexWrap="wrap"
              sx={{ flexDirection: { xs: "column", sm: "row" } }}
            >
              {/* Joining Date */}
              {canViewField("joining_date") && (
              <SoftBox sx={{ width: { xs: "100%", sm: "45%" } }}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Joining Date
                </SoftTypography>
                <SoftInput
                  type="date"
                  name="joining_date"
                  value={formData.joining_date}
                  onChange={handleChange}
                  required={formData.status === "training" || formData.status === "selected"}
                  fullWidth
                />

              </SoftBox>
              )}

              {/* Appointment Date */}
              {canViewField("appointment_date") && (
              <SoftBox sx={{ width: { xs: "100%", sm: "45%" } }}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Appointment Date
                </SoftTypography>
                <SoftInput
                  type="date"
                  name="appointment_date"
                  value={formData.appointment_date}
                  onChange={handleChange}
                  required={formData.status === "selected"}
                  fullWidth
                />
              </SoftBox>
              )}
            </SoftBox>
          )}

          {/* Employee Code */}
          {canViewField("user_code") && (
          <SoftBox mb={2} sx={{ width: "100%" }}>
            <SoftTypography variant="caption" fontWeight="bold">
              Employee Code
            </SoftTypography>
            <SoftInput
              type="text"
              name="user_code"
              value={formData.user_code}
              onChange={handleChange}
              required
              fullWidth
            />
          </SoftBox>
          )}

          {/* Weekend Off */}
          {canViewField("weekend_off") && (
          <SoftBox mb={2} sx={{ width: "100%" }}>
            <SoftTypography variant="caption" fontWeight="bold">
              Weekend Off
            </SoftTypography>
            <SoftBox
              display="flex"
              flexWrap="wrap"
              gap={1.25}
              mt={0.75}
              style={{
                padding: "8px 10px",
                border: "1px solid #d2d6da",
                borderRadius: "6px",
              }}
            >
              {weekendOffOptions.map((day) => {
                const selectedDays = normalizeWeekendOffValue(formData.weekend_off);

                return (
                  <label
                    key={day}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDays.includes(day)}
                      onChange={() => handleWeekendOffToggle(day)}
                    />
                    {day}
                  </label>
                );
              })}
            </SoftBox>
          </SoftBox>
          )}


          {/* Form Actions */}
          <SoftBox mt={3} mr={10} display="flex" justifyContent="flex-end" gap={2}>
            {canOpenList && (
            <SoftButton
              color="info"
              onClick={() => {
                resetForm();
                navigate("/users/list");
              }}
              disabled={loading}
            >
              Cancel
            </SoftButton>
            )}
            {canSubmit && (
            <SoftButton type="submit" color="info" disabled={loading}>
              {loading ? "Processing..." : "Add User"}
            </SoftButton>
            )}
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
};

export default AddUser;
